import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AccordionItem from './components/AccordionItem';

const FAQ: React.FC = () => {
  const { t } = useTranslation('faq');
  const { lang } = useParams<{ lang: string }>();
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prevOpenItems => 
      prevOpenItems.includes(index)
        ? prevOpenItems.filter(item => item !== index)
        : [...prevOpenItems, index]
    );
  };

  // Get the total number of FAQ items
  const totalFaqItems = Object.keys(t('items', { returnObjects: true }) || {}).length;

  return (
    <>
      <Helmet>
        <title>Frequently Asked Questions - ShiftPic</title>
        <meta name="description" content="Find answers to common questions about ShiftPic - the free online image converter that works directly in your browser without uploading files." />
        <meta name="keywords" content="image converter faq, ShiftPic help, image format conversion questions, free image converter help, online image converter support, png to webp, webp converter, image batch conversion" />
        <link rel="canonical" href={`https://shiftpic.com/${lang || 'en'}/faq/`} />
        <meta property="og:title" content="Frequently Asked Questions - ShiftPic" />
        <meta property="og:description" content="Find answers to common questions about ShiftPic - the free online image converter that works directly in your browser without uploading files." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://shiftpic.com/${lang || 'en'}/faq/`} />
        <meta property="og:image" content="https://shiftpic.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Frequently Asked Questions - ShiftPic" />
        <meta name="twitter:description" content="Find answers to common questions about ShiftPic - the free online image converter that works directly in your browser without uploading files." />
        <meta name="twitter:image" content="https://shiftpic.com/og-image.jpg" />
        {/* Add FAQPage structured data for better SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": Array.from({ length: totalFaqItems }).map((_, index) => ({
              "@type": "Question",
              "name": t(`items.${index}.question`),
              "acceptedAnswer": {
                "@type": "Answer",
                "text": t(`items.${index}.answer`)
              }
            }))
          })}
        </script>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="mt-8 mb-12">
            <h1>{t('pageTitle')}</h1>
            <p className="text-lg text-gray-600 max-w-3xl">{t('pageDescription')}</p>
          </div>

          <div className="space-y-4" role="list">
            {Array.from({ length: totalFaqItems }).map((_, index) => (
              <AccordionItem 
                key={index}
                question={t(`items.${index}.question`)}
                answer={t(`items.${index}.answer`)}
                isOpen={openItems.includes(index)}
                onToggle={() => toggleItem(index)}
                index={index}
              />
            ))}
          </div>
        </div>
    </>
  );
};

export default FAQ;